/**
 * La file de capture hors ligne — docs/adr/0001.
 *
 * **Seule la CRÉATION d'une bulle est autorisée hors ligne**, et c'est tout le contenu de
 * cette décision. La capture est le geste le plus fréquent de l'app, et le seul dont
 * l'échec fait perdre l'idée : on n'écrit pas deux fois ce à quoi on a pensé dans le bus.
 *
 * Tout le reste échoue proprement : pas d'édition, pas de coche, pas de calcul. Le serveur
 * reste la seule source de vérité pour tout ce qui se calcule — aucun moteur dupliqué,
 * aucune résolution de conflits, aucun risque de deux dates au plus tôt divergentes.
 *
 * Ce qu'on accepte de perdre : cocher une bulle dans le métro. Ça se reporte de dix minutes
 * sans rien détruire — contrairement à une idée.
 *
 * Écrit à la main sur IndexedDB, sans dépendance : le plus grand risque de ce projet vient
 * des dépendances, pas du code (SPEC §10).
 *
 * **Chaque capture appartient à l'utilisateur qui l'a faite** (ticket 01 du chantier comptes) :
 * seules les siennes se montrent et repartent, celles d'un autre se jettent. Les règles sont
 * dans `fileParUtilisateur.ts`.
 */

import { arreteLaFile, partagerLaFile, type CaptureRangee } from './fileParUtilisateur';

const BASE = 'planification';
const MAGASIN = 'capture';
const VERSION = 1;

/** `cle` (locale, distincte de l'identifiant que le serveur donnera), `poseeLe` et `utilisateur` : `CaptureRangee`. */
export interface CaptureEnAttente extends CaptureRangee {
  titre: string;
  parentId: string | null;
  /**
   * Une Idée (l'état En réflexion), choisie au + du téléphone (chantier
   * `telephone-projets-fiche`, ticket 05) : toujours une création, elle repart en idée.
   */
  idee?: boolean;
}

/** Créer ce qui attendait : le titre, la mère, et si c'est une idée. */
export type CreerDepuisLaFile = (titre: string, parentId: string | null, idee: boolean) => Promise<unknown>;

function ouvrir(): Promise<IDBDatabase> {
  return new Promise((resoudre, rejeter) => {
    const demande = indexedDB.open(BASE, VERSION);
    demande.onupgradeneeded = () => {
      const base = demande.result;
      if (!base.objectStoreNames.contains(MAGASIN)) {
        base.createObjectStore(MAGASIN, { keyPath: 'cle' });
      }
    };
    demande.onsuccess = () => resoudre(demande.result);
    demande.onerror = () => rejeter(demande.error);
  });
}

/**
 * IndexedDB peut être refusé — navigation privée, stockage bloqué, quota plein.
 * Dans ce cas on ne casse rien : la capture échoue comme avant, et l'interface le dit.
 */
async function avecLaBase<T>(travail: (base: IDBDatabase) => Promise<T>, sinon: T): Promise<T> {
  try {
    const base = await ouvrir();
    try {
      return await travail(base);
    } finally {
      base.close();
    }
  } catch {
    return sinon;
  }
}

function enPromesse<T>(demande: IDBRequest<T>): Promise<T> {
  return new Promise((resoudre, rejeter) => {
    demande.onsuccess = () => resoudre(demande.result);
    demande.onerror = () => rejeter(demande.error);
  });
}

export async function poserEnAttente(
  titre: string,
  parentId: string | null,
  utilisateur: string,
  idee = false
): Promise<boolean> {
  return avecLaBase(async (base) => {
    const capture: CaptureEnAttente = {
      cle: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      titre,
      parentId,
      poseeLe: Date.now(),
      utilisateur,
      ...(idee ? { idee: true } : {}),
    };
    const transaction = base.transaction(MAGASIN, 'readwrite');
    await enPromesse(transaction.objectStore(MAGASIN).add(capture));
    return true;
  }, false);
}

async function lireTout(): Promise<CaptureEnAttente[]> {
  return avecLaBase(async (base) => {
    const transaction = base.transaction(MAGASIN, 'readonly');
    return (await enPromesse(transaction.objectStore(MAGASIN).getAll())) as CaptureEnAttente[];
  }, []);
}

/**
 * Les captures de cet utilisateur qui attendent de partir, dans l'ordre où il y a pensé : la
 * feuille garde cette mémoire (SPEC §5.4). Personne de connecté : rien.
 */
export async function lireLaFile(utilisateur: string | null): Promise<CaptureEnAttente[]> {
  return partagerLaFile(await lireTout(), utilisateur).aMoi;
}

async function retirer(cle: string): Promise<void> {
  await avecLaBase(async (base) => {
    const transaction = base.transaction(MAGASIN, 'readwrite');
    await enPromesse(transaction.objectStore(MAGASIN).delete(cle));
    return true;
  }, false);
}

/**
 * Rejouer la file de `utilisateur`, **dans l'ordre**, et jeter celles des autres.
 *
 * L'ordre compte : une bulle capturée dans une autre pourrait dépendre d'un parent créé
 * juste avant. On s'arrête au premier échec réseau plutôt que de sauter des lignes — elles
 * repartiront à la prochaine occasion, et rien ne se perd.
 *
 * `quiEstConnecte` rend l'utilisateur de la session qui portera les requêtes, relu au serveur
 * au moment de rejouer (`useRejouerLaFile`), jamais une valeur gardée depuis longtemps. Null :
 * personne, ou pas celui qu'on attendait, et rien ne part.
 *
 * **Un rejeu à la fois.** L'ouverture de l'app et l'événement `online` peuvent le lancer
 * ensemble ; deux rejeux qui lisent la même file créent chaque bulle deux fois (vu à la
 * vérification du ticket 01 du chantier comptes). Le second attend la fin du premier, puis
 * relit la file.
 */
let rejeuEnCours: Promise<unknown> = Promise.resolve();

export function rejouerLaFile(
  quiEstConnecte: () => Promise<string | null>,
  creer: CreerDepuisLaFile
): Promise<number> {
  const rejeu = rejeuEnCours.then(() => rejouer(quiEstConnecte, creer));
  rejeuEnCours = rejeu.catch(() => undefined);
  return rejeu;
}

async function rejouer(
  quiEstConnecte: () => Promise<string | null>,
  creer: CreerDepuisLaFile
): Promise<number> {
  const utilisateur = await quiEstConnecte();
  if (!utilisateur) return 0;
  const { aMoi: file, aJeter } = partagerLaFile(await lireTout(), utilisateur);
  for (const capture of aJeter) await retirer(capture.cle);
  let parties = 0;

  for (const capture of file) {
    try {
      await creer(capture.titre, capture.parentId, capture.idee === true);
      await retirer(capture.cle);
      parties += 1;
    } catch (erreur) {
      // Un refus du serveur (parent supprimé entre-temps) ne doit pas bloquer la file
      // pour toujours : on retire la capture et on continue. Une panne réseau, ou la
      // session tombée en route, arrête tout — les suivantes attendront.
      const code = erreur instanceof Error && 'code' in erreur ? String(erreur.code) : '';
      if (arreteLaFile(code)) break;
      await retirer(capture.cle);
    }
  }

  return parties;
}

/**
 * Vider la file — a la deconnexion.
 *
 * Les titres captures dorment en clair dans IndexedDB. Sur un appareil partage, les laisser
 * apres un « se deconnecter » revient a montrer a la personne suivante ce a quoi on pensait
 * dans le bus. Rien de ce qui est parti n'est perdu pour autant : ce qui est encore la
 * n'avait pas atteint le serveur, et c'est le prix assume de la deconnexion.
 */
export async function viderLaFile(): Promise<void> {
  await avecLaBase(async (base) => {
    const transaction = base.transaction(MAGASIN, 'readwrite');
    await enPromesse(transaction.objectStore(MAGASIN).clear());
    return true;
  }, false);
}
